import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import InfoIcon from '@/shared/assets/info-icon.svg?react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/shared/utils';

const HOVER_CLOSE_DELAY_MS = 150;

type InfoHintProps = {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  triggerLabel?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  contentClassName?: string;
};

export function InfoHint({
  title,
  icon,
  children,
  triggerLabel,
  side = 'top',
  align = 'center',
  className,
  contentClassName,
}: InfoHintProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const closeTimerRef = useRef<number | undefined>(undefined);

  const cancelClose = () => window.clearTimeout(closeTimerRef.current);

  useEffect(() => cancelClose, []);

  const handleOpenChange = (open: boolean) => {
    cancelClose();
    setIsOpen(open);
    if (!open) setIsPinned(false);
  };

  const handlePointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    cancelClose();
    setIsOpen(true);
  };

  const handlePointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse' || isPinned) return;
    cancelClose();
    closeTimerRef.current = window.setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY_MS
    );
  };

  const handleTriggerClick = (event: MouseEvent) => {
    event.preventDefault();
    cancelClose();
    if (isPinned) {
      setIsOpen(false);
      setIsPinned(false);
      return;
    }
    setIsOpen(true);
    setIsPinned(true);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel ?? title}
          onClick={handleTriggerClick}
          onMouseDown={event => event.preventDefault()}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          className={cn(
            'inline-flex shrink-0 items-center justify-center text-secondary hover:text-primary transition-colors cursor-pointer',
            className
          )}>
          <InfoIcon className="size-[1.6rem]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={16}
        onOpenAutoFocus={event => event.preventDefault()}
        onCloseAutoFocus={event => event.preventDefault()}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={cn(
          'flex w-[28.8rem] max-w-[calc(100vw-3.2rem)] flex-col gap-[0.4rem] rounded-[1.6rem] border-stroke bg-background px-[1.6rem] py-[1.2rem] shadow-dropdown!',
          contentClassName
        )}>
        <div className="flex items-center gap-[0.8rem] text-primary">
          {icon && (
            <span className="flex size-[1.6rem] shrink-0 items-center justify-center [&>svg]:size-full">
              {icon}
            </span>
          )}
          <span className="medium5">{title}</span>
        </div>
        <div className="p6 text-secondary">{children}</div>
      </PopoverContent>
    </Popover>
  );
}
